import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../context/axiosConfig";
import requireAuth from "../context/requireAuth";
import MaterialReactTable from "material-react-table";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ExportToCsv } from "export-to-csv";

function Maintenance() {
  const org_id = localStorage.getItem("organization");

  /* ───────────────  DATA & CATEGORY  ─────────────── */
  const [maintenance, setMaintenance] = useState([]);
  const [originalMaintenance, setOriginalMaintenance] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPlotNo, setSelectedPlotNo] = useState('');
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [showOnlyDestroyed, setShowOnlyDestroyed] = useState(false);

  // Fetch plots data for the dropdown
  useEffect(() => {
    axios.get(`orgs/${org_id}/plots/`, { withCredentials: true })
      .then(res => {
        setPlots(res.data);
      })
      .catch(err => console.error('Error fetching plots:', err));
  }, [org_id]);

  /* load maintenance records with optional filters */
  useEffect(() => {
    setLoading(true);
    let url = `orgs/${org_id}/maintenance`;
    const params = new URLSearchParams();
    
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (selectedPlotNo) params.append('plot_no', selectedPlotNo);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    axios
      .get(url, { withCredentials: true })
      .then((res) => {
        setMaintenance(res.data);
        setOriginalMaintenance(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching maintenance records:', err);
        setMaintenance([]);
        setOriginalMaintenance([]);
        setLoading(false);
      });
  }, [org_id, fromDate, toDate, selectedPlotNo]);

  /* one Set per dynamic column group */
  const [baseVisible, setBaseVisible] = useState(new Set());
  const [phenotypingVisible, setPhenotypingVisible] = useState(new Set());
  const [measurementVisible, setMeasurementVisible] = useState(new Set());
  const [giftVisible, setGiftVisible] = useState(new Set());
  const [drainVisible, setDrainVisible] = useState(new Set());
  const [substrateVisible, setSubstrateVisible] = useState(new Set());
  const [diseaseVisible, setDiseaseVisible] = useState(new Set());
  const [mutationVisible, setMutationVisible] = useState(new Set());
  const [nutrientVisible, setNutrientVisible] = useState(new Set());
  const [pestVisible, setPestVisible] = useState(new Set());
  const [stressorVisible, setStressorVisible] = useState(new Set());
  const [otherVisible, setOtherVisible] = useState(new Set());
  const [destructionVisible, setDestructionVisible] = useState(new Set());
  const [applicationVisible, setApplicationVisible] = useState(new Set());
  const [chemicalVisible, setChemicalVisible] = useState(new Set());
  const [harvestVisible, setHarvestVisible] = useState(new Set());
  const [postharvestVisible, setPostharvestVisible] = useState(new Set());
  const [manipulationVisible, setManipulationVisible] = useState(new Set());
  const [documentationVisible, setDocumentationVisible] = useState(new Set());

  /* ───────────────  FETCHERS  ─────────────── */
  const fetcher = useCallback(
    (endpoint) =>
      axios
        .get(endpoint, { withCredentials: true })
        .then((res) => setMaintenance(res.data))
        .catch(console.error),
    []
  );

  const loadPhenotyping = () =>
    fetcher(`orgs/${org_id}/maintenance/phenotyping/list`);
  const loadMeasurement = () =>
    fetcher(`orgs/${org_id}/maintenance/measurement/list`);
  const loadGift = () =>
    fetcher(`orgs/${org_id}/maintenance/irrigation/gift/list`);
  const loadDrain = () =>
    fetcher(`orgs/${org_id}/maintenance/irrigation/drain/list`);
  const loadSubstrate = () =>
    fetcher(`orgs/${org_id}/maintenance/irrigation/substrate/list`);
  const loadDestruction = () =>
    fetcher(`orgs/${org_id}/maintenance/destruction/list`);
  const loadApplication = () =>
    fetcher(`orgs/${org_id}/maintenance/application/list`);
  const loadChemical = () =>
    fetcher(`orgs/${org_id}/maintenance/chemical-analysis/list`);
  const loadHarvest = () => fetcher(`orgs/${org_id}/maintenance/harvest/list`);
  const loadPostharvest = () =>
    fetcher(`orgs/${org_id}/maintenance/postharvest/list`);
  const loadManipulation = () =>
    fetcher(`orgs/${org_id}/maintenance/manipulation/list`);
  const loadDocumentation = () =>
    fetcher(`orgs/${org_id}/maintenance/documentation/list`);

  /* scouting fetchers */
  const loadDisease = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/disease/list`);
  const loadMutation = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/mutation/list`);
  const loadNutrient = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/nutrient/list`);
  const loadPest = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/pest/list`);
  const loadStressor = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/stressor/list`);
  const loadOther = () =>
    fetcher(`orgs/${org_id}/maintenance/scouting/other/list`);

  /* ───────────────  COLUMN DEFINITIONS  ─────────────── */
  const makeTextCell = () => ({
    Cell: ({ cell }) => String(cell.getValue() ?? "N/A"),
  });

  const baseDefs = [
    { 
      key: "org_plant_no", 
      header: "Plant ID",
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value !== null && value !== undefined ? String(value) : "N/A";
      },
      filterFn: 'equals'
    },
    {
      key: "type",
      header: "Type",
      Cell: ({ cell }) =>
        selectedCategory === "all" ? cell.getValue() : selectedCategory,
    },
    { key: "notes", header: "Description" },
    {
      key: "performed_at",
      header: "Maintenance Date",
      Cell: ({ cell }) => {
        const d = cell.getValue();
        return d
          ? new Date(d).toLocaleString("en-US", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "NO DATE";
      },
    },
    { key: "performed_by", header: "Performed by" },
  ];

  const phenotypingDefs = [
    { key: "sex", header: "Sex" },
    { key: "germination_speed", header: "Germination Speed" },
    {
      key: "aromas",
      header: "Aromas",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((a, i) => (
              <div key={i}>
                {a.aroma_type_name}: {a.intensity}
              </div>
            ))
          : "N/A";
      },
    },
    {
      key: "flower_architectures",
      header: "Flower Architectures",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((x, i) => <div key={i}>{x}</div>)
          : "N/A";
      },
    },
    {
      key: "flower_colours",
      header: "Flower Colours",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((x, i) => <div key={i}>{x}</div>)
          : "N/A";
      },
    },
    { key: "flower_density", header: "Flower Density" },
    {
      key: "infection_types",
      header: "Infection Types",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((x, i) => <div key={i}>{x}</div>)
          : "N/A";
      },
    },
    { key: "internode_spacing", header: "Internode Spacing" },
    {
      key: "leaf_formations",
      header: "Leaf Formations",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((x, i) => <div key={i}>{x}</div>)
          : "N/A";
      },
    },
    ...[0, 1, 2].map((n) => ({
      key: `nutrient_imbalances[${n}]`,
      header: `Nutrient Imbalance ${n + 1}`,
      Cell: ({ row }) => {
        const e = row.original.nutrient_imbalances?.[n];
        return e ? `${e.element_name}: ${e.imbalance_type}` : "—";
      },
    })),
    {
      key: "plant_architectures",
      header: "Plant Architectures",
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return Array.isArray(v)
          ? v.map((x, i) => <div key={i}>{x}</div>)
          : "N/A";
      },
    },
    { key: "trichome_expression", header: "Trichome Expression" },
  ];

  /** 3. Measurement (toggleable) */
  const measurementDefs = [
    { key: "branch_count", header: "Branch Count" },
    { key: "flower_thickness_mm", header: "Flower Thickness (mm)" },
    { key: "height_cm", header: "Height (cm)" },
    { key: "height_from", header: "Height From" },
    { key: "height_to", header: "Height To" },
    { key: "lai_m2m2", header: "LAI (m²/m²)" },
    { key: "light_blue", header: "Light Blue" },
    { key: "light_farred", header: "Light Far Red" },
    { key: "light_green", header: "Light Green" },
    { key: "light_measured_at", header: "Light Measured At" },
    { key: "light_ppfd", header: "Light PPFD" },
    { key: "light_red", header: "Light Red" },
    { key: "node_count", header: "Node Count" },
    { key: "planting_density_m2", header: "Planting Density (m²)" },
    { key: "stem_diameter_mm", header: "Stem Diameter (mm)" },
    { key: "weight_g", header: "Weight (g)" },
    { key: "weight_includes", header: "Weight Includes" },
    { key: "width_cm", header: "Width (cm)" },
  ];

  const giftDefs = [
    { key: "ec", header: "EC", ...makeTextCell() },
    { key: "ph", header: "pH", ...makeTextCell() },
    { key: "water_temp", header: "Water Temperature (°C)", ...makeTextCell() },
    { key: "shot_size", header: "Shot Size (mL)", ...makeTextCell() },
    {
      key: "shot_frequency",
      header: "Shot Frequency (min)",
      ...makeTextCell(),
    },
    {
      key: "nutrient_recipe_name",
      header: "Nutrient Recipe",
      ...makeTextCell(),
    },
  ];

  const drainDefs = [
    { key: "drain_volume", header: "Drain Volume", ...makeTextCell() },
    { key: "ec", header: "EC", ...makeTextCell() },
    { key: "ph", header: "pH", ...makeTextCell() },
  ];
  const substrateDefs = [
    { key: "ec", header: "EC", ...makeTextCell() },
    { key: "ph", header: "pH", ...makeTextCell() },
    {
      key: "substrate_temp",
      header: "Substrate Temperature",
      ...makeTextCell(),
    },
    { key: "water_content", header: "Water Content", ...makeTextCell() },
  ];

  /* ─── Scouting sub-type defs ─── */
  const diseaseDefs = [
    { key: "disease_type_name", header: "Disease Type", ...makeTextCell() },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];
  const mutationDefs = [
    { key: "mutation_type_name", header: "Mutation Type", ...makeTextCell() },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];
  const nutrientDefs = [
    { key: "nutrient_element_name", header: "Element", ...makeTextCell() },
    {
      key: "toxicity_or_deficiency",
      header: "Toxic/Deficient",
      ...makeTextCell(),
    },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];
  const pestDefs = [
    { key: "pest_type_name", header: "Pest Type", ...makeTextCell() },
    { key: "pest_type_id", header: "Pest ID", ...makeTextCell() },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];
  const stressorDefs = [
    { key: "stressor_type_name", header: "Stressor", ...makeTextCell() },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];
  const otherDefs = [
    { key: "notes", header: "Notes", ...makeTextCell() },
    { key: "severity", header: "Severity", ...makeTextCell() },
    { key: "alarm_level", header: "Alarm Level", ...makeTextCell() },
  ];

  const destructionDefs = [
    { key: "plant_material", header: "Plant Material", ...makeTextCell() },
    { key: "reason", header: "Reason", ...makeTextCell() },
    { key: "reason_other_text", header: "Reason (Other)", ...makeTextCell() },
    { key: "weight_g", header: "Weight (g)", ...makeTextCell() },
  ];

  const applicationDefs = [
    { key: "product", header: "Product", ...makeTextCell() },
    { key: "application_type", header: "Type", ...makeTextCell() },
    { key: "application_mode", header: "Mode", ...makeTextCell() },
    { key: "dose", header: "Dose", ...makeTextCell() },
    { key: "dose_unit", header: "Dose Unit", ...makeTextCell() },
    { key: "quantity", header: "Quantity", ...makeTextCell() },
    { key: "quantity_unit", header: "Quantity Unit", ...makeTextCell() },
  ];

  const chemicalDefs = [
    { key: "method", header: "Method", ...makeTextCell() },
    // CBD total
    {
      key: "CBD total_rep1",
      header: "CBD total Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBD total_rep1"] ?? "N/A";
      },
    },
    {
      key: "CBD total_rep2",
      header: "CBD total Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBD total_rep2"] ?? "N/A";
      },
    },
    {
      key: "CBD total_rep3",
      header: "CBD total Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBD total_rep3"] ?? "N/A";
      },
    },
    // CBDa
    {
      key: "CBDa_rep1",
      header: "CBDa Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBDa_rep1"] ?? "N/A";
      },
    },
    {
      key: "CBDa_rep2",
      header: "CBDa Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBDa_rep2"] ?? "N/A";
      },
    },
    {
      key: "CBDa_rep3",
      header: "CBDa Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBDa_rep3"] ?? "N/A";
      },
    },
    // CBG total
    {
      key: "CBG total_rep1",
      header: "CBG total Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBG total_rep1"] ?? "N/A";
      },
    },
    {
      key: "CBG total_rep2",
      header: "CBG total Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBG total_rep2"] ?? "N/A";
      },
    },
    {
      key: "CBG total_rep3",
      header: "CBG total Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["CBG total_rep3"] ?? "N/A";
      },
    },
    // Moisture content
    {
      key: "Moisture content_rep1",
      header: "Moisture content Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Moisture content_rep1"] ?? "N/A";
      },
    },
    {
      key: "Moisture content_rep2",
      header: "Moisture content Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Moisture content_rep2"] ?? "N/A";
      },
    },
    {
      key: "Moisture content_rep3",
      header: "Moisture content Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Moisture content_rep3"] ?? "N/A";
      },
    },
    // THC total
    {
      key: "THC total_rep1",
      header: "THC total Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THC total_rep1"] ?? "N/A";
      },
    },
    {
      key: "THC total_rep2",
      header: "THC total Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THC total_rep2"] ?? "N/A";
      },
    },
    {
      key: "THC total_rep3",
      header: "THC total Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THC total_rep3"] ?? "N/A";
      },
    },
    // THCa
    {
      key: "THCa_rep1",
      header: "THCa Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THCa_rep1"] ?? "N/A";
      },
    },
    {
      key: "THCa_rep2",
      header: "THCa Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THCa_rep2"] ?? "N/A";
      },
    },
    {
      key: "THCa_rep3",
      header: "THCa Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["THCa_rep3"] ?? "N/A";
      },
    },
    // Terpenoids total
    {
      key: "Terpenoids total_rep1",
      header: "Terpenoids total Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Terpenoids total_rep1"] ?? "N/A";
      },
    },
    {
      key: "Terpenoids total_rep2",
      header: "Terpenoids total Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Terpenoids total_rep2"] ?? "N/A";
      },
    },
    {
      key: "Terpenoids total_rep3",
      header: "Terpenoids total Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Terpenoids total_rep3"] ?? "N/A";
      },
    },
    // Water activity
    {
      key: "Water activity (aW)_rep1",
      header: "Water activity (aW) Rep 1",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Water activity (aW)_rep1"] ?? "N/A";
      },
    },
    {
      key: "Water activity (aW)_rep2",
      header: "Water activity (aW) Rep 2",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Water activity (aW)_rep2"] ?? "N/A";
      },
    },
    {
      key: "Water activity (aW)_rep3",
      header: "Water activity (aW) Rep 3",
      Cell: ({ row }) => {
        const results = row.original.results_json;
        return results?.["Water activity (aW)_rep3"] ?? "N/A";
      },
    },
  ];

  const harvestDefs = [
    { key: "plant_weight_g", header: "Plant Weight (g)", ...makeTextCell() },
    { key: "leaf_weight_g", header: "Leaf Weight (g)", ...makeTextCell() },
    {
      key: "defoliated_weight_g",
      header: "Defoliated Weight (g)",
      ...makeTextCell(),
    },
    {
      key: "infected_weight_g",
      header: "Infected Weight (g)",
      ...makeTextCell(),
    },
    { key: "infected_reason", header: "Infected Reason", ...makeTextCell() },
  ];

  /** 11. Post-Harvest */
  const postharvestDefs = [
    { key: "plant_weight_dry_g", header: "Plant Dry (g)", ...makeTextCell() },
    { key: "leaf_weight_dry_g", header: "Leaf Dry (g)", ...makeTextCell() },
    { key: "stem_weight_dry_g", header: "Stem Dry (g)", ...makeTextCell() },
    {
      key: "infected_weight_dry_g",
      header: "Infected Dry (g)",
      ...makeTextCell(),
    },
    { key: "trim_weight_g", header: "Trim (g)", ...makeTextCell() },
    {
      key: "trimmed_flower_g",
      header: "Trimmed Flower (g)",
      ...makeTextCell(),
    },
    {
      key: "untrimmed_flower_g",
      header: "Untrimmed Flower(g)",
      ...makeTextCell(),
    },
  ];

  const manipulationDefs = [
    { key: "technique_name", header: "Technique", ...makeTextCell() },
  ];

  const documentationDefs = [
    { key: "notes", header: "Notes", ...makeTextCell() },
    /* Render a small image directly from the Base-64 string */
    {
      key: "image_base64",
      header: "Image",
      Cell: ({ cell }) => {
        const b64 = cell.getValue();
        if (!b64) return "—";
        return (
          <img
            src={`data:image/jpeg;base64,${b64}`}
            alt="doc"
            style={{ maxWidth: 160, maxHeight: 160, objectFit: "cover" }}
          />
        );
      },
    },
  ];

  /* helper to build extra cols */
  const extraCols = (defs, vis) =>
    defs
      .filter((d) => vis.has(d.key))
      .map(({ key, header, Cell, filterFn }) => ({ 
        accessorKey: key, 
        header, 
        Cell,
        ...(filterFn && { filterFn })
      }));

  const hasNonEmpty = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'number') return !Number.isNaN(v);
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  };

  const currentCategoryVisibleSet = useMemo(() => {
    switch (selectedCategory) {
      case 'phenotyping': return phenotypingVisible;
      case 'measurement': return measurementVisible;
      case 'gift': return giftVisible;
      case 'drain': return drainVisible;
      case 'substrate': return substrateVisible;
      case 'disease': return diseaseVisible;
      case 'mutation': return mutationVisible;
      case 'nutrient': return nutrientVisible;
      case 'pest': return pestVisible;
      case 'stressor': return stressorVisible;
      case 'other': return otherVisible;
      case 'destruction': return destructionVisible;
      case 'application': return applicationVisible;
      case 'chemical-analysis': return chemicalVisible;
      case 'harvest': return harvestVisible;
      case 'postharvest': return postharvestVisible;
      case 'manipulation': return manipulationVisible;
      case 'documentation': return documentationVisible;
      default: return new Set();
    }
  }, [
    selectedCategory,
    phenotypingVisible, measurementVisible, giftVisible, drainVisible, substrateVisible,
    diseaseVisible, mutationVisible, nutrientVisible, pestVisible, stressorVisible,
    otherVisible, destructionVisible, applicationVisible, chemicalVisible, harvestVisible,
    postharvestVisible, manipulationVisible, documentationVisible
  ]);

  const rowMatchesSelectedKeys = (row) => {
    // no category or no selected columns → don't filter rows out
    if (selectedCategory === 'all') return true;
    const keys = Array.from(currentCategoryVisibleSet);
    if (keys.length === 0) return true;

    // keep row if it has a value for at least one selected column
    for (const key of keys) {
      let value;
      if (selectedCategory === 'chemical-analysis') {
        value = row.results_json?.[key];        // chemical columns read from results_json
      } else if (/^nutrient_imbalances\[(\d+)\]$/.test(key)) {
        const idx = parseInt(key.match(/\[(\d+)\]/)?.[1] ?? '0', 10);
        const imb = row.nutrient_imbalances?.[idx];
        value = imb && (imb.element_name || imb.imbalance_type);
      } else {
        value = row[key];
      }
      if (hasNonEmpty(value)) return true;
    }
    return false;
  };


  /* ───────────────  MERGE COLUMNS  ─────────────── */
  const columns = useMemo(() => {
    const baseCols = baseDefs
      .filter((c) => baseVisible.has(c.key))
      .map(({ key, header, Cell, filterFn }) => ({ 
        accessorKey: key, 
        header, 
        Cell,
        ...(filterFn && { filterFn })
      }));

    switch (selectedCategory) {
      case "phenotyping":
        return [...baseCols, ...extraCols(phenotypingDefs, phenotypingVisible)];
      case "measurement":
        return [...baseCols, ...extraCols(measurementDefs, measurementVisible)];
      case "gift":
        return [...baseCols, ...extraCols(giftDefs, giftVisible)];
      case "drain":
        return [...baseCols, ...extraCols(drainDefs, drainVisible)];
      case "substrate":
        return [...baseCols, ...extraCols(substrateDefs, substrateVisible)];
      case "disease":
        return [...baseCols, ...extraCols(diseaseDefs, diseaseVisible)];
      case "mutation":
        return [...baseCols, ...extraCols(mutationDefs, mutationVisible)];
      case "nutrient":
        return [...baseCols, ...extraCols(nutrientDefs, nutrientVisible)];
      case "pest":
        return [...baseCols, ...extraCols(pestDefs, pestVisible)];
      case "stressor":
        return [...baseCols, ...extraCols(stressorDefs, stressorVisible)];
      case "other":
        return [...baseCols, ...extraCols(otherDefs, otherVisible)];
      case "destruction":
        return [...baseCols, ...extraCols(destructionDefs, destructionVisible)];
      case "application":
        return [...baseCols, ...extraCols(applicationDefs, applicationVisible)];
      case "chemical-analysis":
        return [...baseCols, ...extraCols(chemicalDefs, chemicalVisible)];
      case "harvest":
        return [...baseCols, ...extraCols(harvestDefs, harvestVisible)];
      case "postharvest":
        return [...baseCols, ...extraCols(postharvestDefs, postharvestVisible)];
      case "manipulation":
        return [
          ...baseCols,
          ...extraCols(manipulationDefs, manipulationVisible),
        ];
      case "documentation":
        return [
          ...baseCols,
          ...extraCols(documentationDefs, documentationVisible),
        ];
      default:
        return baseCols; // "all" shows nothing until base boxes ticked
    }
  }, [
    baseVisible,
    phenotypingVisible,
    measurementVisible,
    giftVisible,
    drainVisible,
    substrateVisible,
    diseaseVisible,
    mutationVisible,
    nutrientVisible,
    pestVisible,
    stressorVisible,
    destructionVisible,
    otherVisible,
    applicationVisible,
    chemicalVisible,
    harvestVisible,
    postharvestVisible,
    manipulationVisible,
    documentationVisible,
    selectedCategory,
  ]);

  const hasVisibleColumns = columns.length > 0;

  /* ───────────────  CSV EXPORTER  ─────────────── */
  const csvOptions = useMemo(() => ({
    fieldSeparator: ",",
    quoteStrings: '"',
    decimalSeparator: ".",
    showLabels: true,
    useBom: true,
    useKeysAsHeaders: true,
    fields: columns.map((c) => c.header),
  }), [columns]);

  const csvExporter = useMemo(() => new ExportToCsv(csvOptions), [csvOptions]);

  // Helper function to extract only the visible column data from a row
  const extractVisibleData = (row) => {
    const visibleData = {};
    columns.forEach(column => {
      const key = column.accessorKey;
      let value = row[key];
      
      // Handle special formatting for complex data types
      if (key === 'aromas' && Array.isArray(value)) {
        value = value.map(a => `${a.aroma_type_name}: ${a.intensity}`).join(', ');
      } else if ((key === 'flower_architectures' || key === 'flower_colours' || 
                  key === 'infection_types' || key === 'leaf_formations' || 
                  key === 'plant_architectures') && Array.isArray(value)) {
        value = value.join(', ');
      } else if (key.includes('nutrient_imbalances[')) {
        // Handle nutrient imbalances specially
        const index = parseInt(key.match(/\[(\d+)\]/)?.[1] || '0');
        const imbalance = row.nutrient_imbalances?.[index];
        value = imbalance ? `${imbalance.element_name}: ${imbalance.imbalance_type}` : '—';
      } else if (key === 'performed_at' && value) {
        value = new Date(value).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } else if (key === 'org_plant_no') {
        value = value !== null && value !== undefined ? String(value) : "N/A";
      } else if (key === 'type' && selectedCategory !== "all") {
        value = selectedCategory;
      } else if (key.includes('_rep1') || key.includes('_rep2') || key.includes('_rep3')) {
        // Handle chemical analysis results
        const results = row.results_json;
        value = results?.[key] ?? "N/A";
      } else if (key === 'image_base64') {
        // For CSV export, just indicate if image exists
        value = value ? 'Image Present' : 'No Image';
      } else if (value === null || value === undefined) {
        value = 'N/A';
      }
      
      visibleData[column.header] = value;
    });
    return visibleData;
  };

  const handleExportAll = () => {
    const filteredData = maintenance.filter((entry) => {
      const entryDate = new Date(entry.performed_at);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      if (from && entryDate < from) return false;
      if (to && entryDate > to) return false;

      if (showOnlyActive) return !entry.destruction_id;
      if (showOnlyDestroyed) return !!entry.destruction_id;
      return true;
    });
    
    const visibleData = filteredData.map(extractVisibleData);
    csvExporter.generateCsv(visibleData);
  };

  const handleExportSelected = (table) => {
    const visibleRowData = table.getSelectedRowModel().rows.map((row) => extractVisibleData(row.original));
    csvExporter.generateCsv(visibleRowData);
  };

  // Master Export handler - exports all maintenance data with all fields
  const handleMasterExport = async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (selectedPlotNo) params.append('plot_no', selectedPlotNo);
      
      const url = `orgs/${org_id}/maintenance/export/master${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `maintenance_master_export_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting master data:', error);
      alert('Failed to export master data. Please try again.');
    }
  };

const filteredRows = useMemo(() => {
  if (!hasVisibleColumns) return [];
  return maintenance.filter((entry) => {
    const entryDate = new Date(entry.performed_at);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    if (from && entryDate < from) return false;
    if (to && entryDate > to) return false;

    if (showOnlyActive) {
      if (entry.destruction_id) return false;
    }
    if (showOnlyDestroyed) {
      if (!entry.destruction_id) return false;
    }
    return rowMatchesSelectedKeys(entry);
  });
}, [
  maintenance,
  hasVisibleColumns,
  fromDate,
  toDate,
  showOnlyActive,
  showOnlyDestroyed,
  rowMatchesSelectedKeys,
]);
  /* generic checkbox builder */
  const CB = (defs, set, vis) => (
    <FormGroup row sx={{ mb: 1 }}>
      {defs.map((col) => (
        <FormControlLabel
          key={col.key}
          control={
            <Checkbox
              size="small"
              className="maintenanceCheckbox maintenanceTableCheckbox"
              checked={vis.has(col.key)}
              onChange={(e) =>
                set((prev) => {
                  const next = new Set(prev);
                  e.target.checked ? next.add(col.key) : next.delete(col.key);
                  return next;
                })
              }
            />
          }
          label={col.header}
        />
      ))}
    </FormGroup>
  );

  /* ───────────────  JSX  ─────────────── */
  return (
    <div className="bodyContent">
      <label className="subheader">Maintenance</label>

      <Paper sx={{ p: 2, mb: 2, boxShadow: 'none' }}>
        <p className="dropdownTitle maintenanceTitle">
            Filter maintenance records
          </p>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="plot-select-label">Plot</InputLabel>
            <Select
              labelId="plot-select-label"
              value={selectedPlotNo}
              onChange={(e) => setSelectedPlotNo(e.target.value)}
              label="Plot"
            >
              <MenuItem value="">
                <em>All Plots</em>
              </MenuItem>
              {plots.filter(plot => plot.plotno !== "1").map((plot) => (
                <MenuItem key={plot.plot_id} value={plot.org_plot_no}>
                  {plot.plotno || `Plot ${plot.org_plot_no}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={() => {
              setFromDate('');
              setToDate('');
              setSelectedPlotNo('');
              setShowOnlyActive(false);
              setShowOnlyDestroyed(false);
              setSelectedCategory('all');
              
              // Reset all column visibility sets
              setBaseVisible(new Set());
              setPhenotypingVisible(new Set());
              setMeasurementVisible(new Set());
              setGiftVisible(new Set());
              setDrainVisible(new Set());
              setSubstrateVisible(new Set());
              setDiseaseVisible(new Set());
              setMutationVisible(new Set());
              setNutrientVisible(new Set());
              setPestVisible(new Set());
              setStressorVisible(new Set());
              setOtherVisible(new Set());
              setDestructionVisible(new Set());
              setApplicationVisible(new Set());
              setChemicalVisible(new Set());
              setHarvestVisible(new Set());
              setPostharvestVisible(new Set());
              setManipulationVisible(new Set());
              setDocumentationVisible(new Set());
              
              // Reset to showing all maintenance records
              setMaintenance(originalMaintenance);
            }}
            variant="outlined"
            size="small"
          >
            Clear Filters
          </Button>
        </Box>

        <FormGroup row sx={{ mb: 1 }} style={{ marginTop: '20px', marginLeft: '5px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  className="maintenanceCheckbox maintenanceTableCheckbox"
                  checked={showOnlyActive}
                  onChange={() => {
                        setShowOnlyActive(prev => !prev);
                        if (!showOnlyActive) setShowOnlyDestroyed(false);
                      }}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                />
              }
              label="Show only active plants"
            />

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  className="maintenanceCheckbox maintenanceTableCheckbox"
                  checked={showOnlyDestroyed}
                  onChange={() => {
                        setShowOnlyDestroyed(prev => !prev);
                        if (!showOnlyDestroyed) setShowOnlyActive(false);
                      }}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                />
              }
              label="Show only destroyed plants"
            />
        </FormGroup>

        {/* Category selector inside the Paper */}
        <Box sx={{ mt: 2 }}>
          <p className="dropdownTitle">
            Category
          </p>
          <select
            className="newMaintenanceInput"
            style={{ width: '40%', marginBottom: 0 }}
            value={selectedCategory}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedCategory(val);

              /* 1️⃣ gather every key that is currently selected anywhere */
              const previouslySelected = new Set([
                ...phenotypingVisible,
                ...measurementVisible,
                ...giftVisible,
                ...drainVisible,
                ...substrateVisible,
                ...diseaseVisible,
                ...mutationVisible,
                ...nutrientVisible,
                ...pestVisible,
                ...stressorVisible,
                ...otherVisible,
                ...destructionVisible,
                ...applicationVisible,
                ...chemicalVisible,
                ...harvestVisible,
                ...postharvestVisible,
                ...manipulationVisible,
                ...documentationVisible,
              ]);
              /* keep the base selections untouched */
              // ─────────────────── reset the per-category sets ───────────────────
              setPhenotypingVisible(new Set());
              setMeasurementVisible(new Set());
              setGiftVisible(new Set());
              setDrainVisible(new Set());
              setSubstrateVisible(new Set());
              setDiseaseVisible(new Set());
              setMutationVisible(new Set());
              setNutrientVisible(new Set());
              setPestVisible(new Set());
              setStressorVisible(new Set());
              setOtherVisible(new Set());
              setDestructionVisible(new Set());
              setApplicationVisible(new Set());
              setChemicalVisible(new Set());
              setHarvestVisible(new Set());
              setPostharvestVisible(new Set());
              setManipulationVisible(new Set());
              setDocumentationVisible(new Set());

              /* 2️⃣ re-apply keys that are common to the new category */
              const repopulate = (defs, setFn) => {
                const next = new Set(
                  defs
                    .filter((d) => previouslySelected.has(d.key))
                    .map((d) => d.key)
                );
                setFn(next);
              };

              switch (val) {
                case "phenotyping":
                  repopulate(phenotypingDefs, setPhenotypingVisible);
                  break;
                case "measurement":
                  repopulate(measurementDefs, setMeasurementVisible);
                  break;
                case "gift":
                  repopulate(giftDefs, setGiftVisible);
                  break;
                case "drain":
                  repopulate(drainDefs, setDrainVisible);
                  break;
                case "substrate":
                  repopulate(substrateDefs, setSubstrateVisible);
                  break;
                case "disease":
                  repopulate(diseaseDefs, setDiseaseVisible);
                  break;
                case "mutation":
                  repopulate(mutationDefs, setMutationVisible);
                  break;
                case "nutrient":
                  repopulate(nutrientDefs, setNutrientVisible);
                  break;
                case "pest":
                  repopulate(pestDefs, setPestVisible);
                  break;
                case "stressor":
                  repopulate(stressorDefs, setStressorVisible);
                  break;
                case "other":
                  repopulate(otherDefs, setOtherVisible);
                  break;
                case "destruction":
                  repopulate(destructionDefs, setDestructionVisible);
                  break;
                case "application":
                  repopulate(applicationDefs, setApplicationVisible);
                  break;
                case "chemical-analysis":
                  repopulate(chemicalDefs, setChemicalVisible);
                  break;
                case "harvest":
                  repopulate(harvestDefs, setHarvestVisible);
                  break;
                case "postharvest":
                  repopulate(postharvestDefs, setPostharvestVisible);
                  break;
                case "manipulation":
                  repopulate(manipulationDefs, setManipulationVisible);
                  break;
                case "documentation":
                  repopulate(documentationDefs, setDocumentationVisible);
                  break;
                default:
              }

              /* 3️⃣ fetch rows or filter local cache */
              ({
                phenotyping: loadPhenotyping,
                measurement: loadMeasurement,
                gift: loadGift,
                drain: loadDrain,
                substrate: loadSubstrate,
                disease: loadDisease,
                mutation: loadMutation,
                nutrient: loadNutrient,
                pest: loadPest,
                stressor: loadStressor,
                other: loadOther,
                destruction: loadDestruction,
                application: loadApplication,
                harvest: loadHarvest,
                postharvest: loadPostharvest,
                "chemical-analysis": loadChemical,
                manipulation: loadManipulation,
                documentation: loadDocumentation,
              })[val]?.() ??
                (val === "all"
                  ? setMaintenance(originalMaintenance)
                  : setMaintenance(
                      originalMaintenance.filter((r) => r.type === val)
                    ));
            }}
          >
            <option value="all">All Categories</option>
            <option value="phenotyping">Phenotyping</option>

            <optgroup label="Irrigation">
              <option value="gift">Gift</option>
              <option value="drain">Drain</option>
              <option value="substrate">Substrate</option>
            </optgroup>

            <optgroup label="Scouting">
              <option value="disease">Disease</option>
              <option value="mutation">Mutation</option>
              <option value="nutrient">Nutrient</option>
              <option value="pest">Pest</option>
              <option value="stressor">Stressor</option>
              <option value="other">Other</option>
            </optgroup>

            <option value="measurement">Measurement</option>
            <option value="destruction">Destruction</option>
            <optgroup label="Harvest/Post-Harvest">
              <option value="harvest">Harvest</option>
              <option value="postharvest">Post Harvest</option>
              <option value="chemical-analysis">Chemical Analysis</option>
            </optgroup>
            <option value="manipulation">Manipulation</option>
            <option value="application">Application &amp; IPM</option>
            <option value="documentation">Documentation/Other</option>
          </select>
        </Box>
      </Paper>

      <div className="plantTableDiv">
        <FormGroup row sx={{ mb: 1 }} style={{ marginLeft: '5px' }}>
        <Stack spacing={0.5}>
          {/* dynamic check-box strips */}
          {CB(baseDefs, setBaseVisible, baseVisible)}
          {selectedCategory === "phenotyping" &&
            CB(phenotypingDefs, setPhenotypingVisible, phenotypingVisible)}
          {selectedCategory === "measurement" &&
            CB(measurementDefs, setMeasurementVisible, measurementVisible)}
          {selectedCategory === "gift" &&
            CB(giftDefs, setGiftVisible, giftVisible)}
          {selectedCategory === "drain" &&
            CB(drainDefs, setDrainVisible, drainVisible)}
          {selectedCategory === "substrate" &&
            CB(substrateDefs, setSubstrateVisible, substrateVisible)}
          {selectedCategory === "disease" &&
            CB(diseaseDefs, setDiseaseVisible, diseaseVisible)}
          {selectedCategory === "mutation" &&
            CB(mutationDefs, setMutationVisible, mutationVisible)}
          {selectedCategory === "nutrient" &&
            CB(nutrientDefs, setNutrientVisible, nutrientVisible)}
          {selectedCategory === "pest" &&
            CB(pestDefs, setPestVisible, pestVisible)}
          {selectedCategory === "stressor" &&
            CB(stressorDefs, setStressorVisible, stressorVisible)}
          {selectedCategory === "other" &&
            CB(otherDefs, setOtherVisible, otherVisible)}
          {selectedCategory === "destruction" &&
            CB(destructionDefs, setDestructionVisible, destructionVisible)}
          {selectedCategory === "application" &&
            CB(applicationDefs, setApplicationVisible, applicationVisible)}
          {selectedCategory === "chemical-analysis" &&
            CB(chemicalDefs, setChemicalVisible, chemicalVisible)}
          {selectedCategory === "harvest" &&
            CB(harvestDefs, setHarvestVisible, harvestVisible)}
          {selectedCategory === "postharvest" &&
            CB(postharvestDefs, setPostharvestVisible, postharvestVisible)}
          {selectedCategory === "manipulation" &&
            CB(manipulationDefs, setManipulationVisible, manipulationVisible)}
          {selectedCategory === "documentation" &&
            CB(
              documentationDefs,
              setDocumentationVisible,
              documentationVisible
            )}
        </Stack>
        </FormGroup>

        <MaterialReactTable
          columns={columns}
          data={filteredRows}
          enableRowSelection
          enableColumnOrdering={hasVisibleColumns}
          state={{ columnVisibility: { "mrt-row-select": hasVisibleColumns } }}
          initialState={{
            showColumnFilters: true,
            pagination: { pageIndex: 0, pageSize: 25 },
            sorting: [{ id: "org_plant_no", desc: true }],
            columnVisibility: { "mrt-row-select": false },
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: "flex", gap: 1, p: 0.5, flexWrap: "wrap" }}>
              <Button
                color="primary"
                variant="contained"
                startIcon={<FileDownloadIcon />}
                disabled={!hasVisibleColumns}
                onClick={handleExportAll}
              >
                Export All Data
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                disabled={
                  !hasVisibleColumns ||
                  (!table.getIsSomeRowsSelected() &&
                    !table.getIsAllRowsSelected())
                }
                onClick={() => handleExportSelected(table)}
              >
                Export Selected Rows
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleMasterExport}
              >
                Master Export
              </Button>
            </Box>
          )}
        />
      </div>
    </div>
  );
}

export default requireAuth(Maintenance);
